import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type Role = 'user' | 'assistant';
export type Phase = 'thinking' | 'typing' | 'done';

export interface AgentMessageDto {
  createdAt: string;
  role: Role | string;
  text: string;
}

export interface AgentAskRequest {
  message: string;
  threadId?: string;
  agentId?: string;
  // add any extra fields you pass to your backend here
}

export interface AgentAskResponse {
  success: boolean;
  threadId: string;
  runId: string;
  status: string;
  messages: AgentMessageDto[];
  message?: string; // error payloads
}

export interface AgentStreamEvent {
  type: 'thread' | 'delta' | 'done';
  threadId?: string;
  delta?: string;
}

@Injectable({ providedIn: 'root' })
export class AoaiService {
  constructor(private http: HttpClient) {}

  // tip: move to environments if you prefer
  private baseUrl = `${environment.apiBaseUrl}/api/v1`;

  /** One-shot (non-streaming) call you already had */
  ask(req: AgentAskRequest): Observable<AgentAskResponse> {
    return this.http.post<AgentAskResponse>(`${this.baseUrl}/agent/ask`, req).pipe(
      map(r => {
        r.messages = r.messages.map(m => ({
          ...m,
          role: (m.role as string).toLowerCase() as Role,
        }));
        return r;
      })
    );
  }

  /**
   * NEW: streaming API.
   * Expects NDJSON/SSE-like lines from `${baseUrl}/agent/ask/stream`
   * Example server output:
   *   data: {"threadId":"thd_123"}\n
   *   data: {"delta":"Hello"}\n
   *   data: {"delta":" world"}\n
   *   [DONE]\n
   */
  askStream(req: AgentAskRequest): Observable<AgentStreamEvent> {
    return new Observable<AgentStreamEvent>(observer => {
      const ctrl = new AbortController();

      fetch(`${this.baseUrl}/agent/ask/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
        signal: ctrl.signal,
      })
        .then(async res => {
          if (!res.ok || !res.body) throw new Error(`${res.status} ${res.statusText}`);

          // optional: capture thread id via response header if present
          const hdrThread = res.headers.get('x-thread-id');
          if (hdrThread) observer.next({ type: 'thread', threadId: hdrThread });

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          const pump = async (): Promise<void> => {
            const { done, value } = await reader.read();
            if (done) {
              observer.next({ type: 'done' });
              observer.complete();
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            let lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            for (const raw of lines) {
              const line = raw.trim();
              if (!line) continue;
              if (line === '[DONE]') {
                observer.next({ type: 'done' });
                observer.complete();
                ctrl.abort();
                return;
              }
              const jsonStr = line.startsWith('data:') ? line.slice(5).trim() : line;
              try {
                const obj = JSON.parse(jsonStr);
                if (obj.threadId) observer.next({ type: 'thread', threadId: obj.threadId });
                if (obj.delta) observer.next({ type: 'delta', delta: obj.delta });
                if (obj.done) {
                  observer.next({ type: 'done' });
                  observer.complete();
                  ctrl.abort();
                  return;
                }
              } catch {
                // plain text token
                observer.next({ type: 'delta', delta: line });
              }
            }

            await pump();
          };

          await pump();
        })
        .catch(err => observer.error(err));

      return () => ctrl.abort();
    });
  }
}
